import { AppRoutes } from '../common/constants';
import { BaseRoleplayer } from '../common/types/client';
import { Box, Card, CardContent, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { getFormattedDate } from '../common/lib/date';
import { GridActionsCellItem, GridActionsCellItemProps } from '@mui/x-data-grid';
import { Icon } from '@iconify/react';
import { JSX, ReactElement, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Roleplayer } from '../common/types/roleplayer';
import {
  useCreateLegalEntityMutation,
  useCreateNaturalPerson,
} from '../common/components/mutations';
import { useNavigate } from 'react-router-dom';
import ClientList from './clientList';
import ClientStatusBadge, { ClientStatusTooltipContent } from './components/clientStatusBadge';
import Content from '../common/components/content';
import Layout from '../common/components/layout';

const breadcrumbs = [{ label: 'Your Clients', href: '/clients' }];

/**
 * Page with the list of clients
 */
const ClientsPage = (): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const [clientsCount, setClientsCount] = useState(0);
  const createNaturalPersonMutation = useCreateNaturalPerson();
  const createLegalEntityMutation = useCreateLegalEntityMutation();

  const handleSelectClient = (client: Roleplayer): void => {
    navigate(
      `${AppRoutes[client.typeName === 'legal' ? 'LegalEntities' : 'NaturalPersons']}/${client.id}`,
    );
  };

  const handleCloseCreationMenu = (): void => setAnchorEl(null);

  const handleCreateNaturalPerson = (): void => {
    handleCloseCreationMenu();
    createNaturalPersonMutation.mutate(
      {},
      {
        onSuccess: (data): void => {
          navigate(`${AppRoutes.NaturalPersons}/${data.roleplayerID}/edit`);
        },
      },
    );
  };

  const handleCreateLegalEntity = (): void => {
    handleCloseCreationMenu();
    createLegalEntityMutation.mutate(
      {},
      {
        onSuccess: (data): void => {
          navigate(`${AppRoutes.LegalEntities}/${data.roleplayerID}/edit`);
        },
      },
    );
  };

  return (
    <Layout breadcrumbs={breadcrumbs}>
      <Content className="space-y-4">
        <Box className="flex justify-between">
          <Typography variant="h2">Your Clients</Typography>
          <LoadingButton
            size="large"
            variant="contained"
            color="secondary"
            endIcon={<Icon icon="uil:plus" />}
            loading={createNaturalPersonMutation.isPending || createLegalEntityMutation.isPending}
            onClick={({ currentTarget }): void => setAnchorEl(currentTarget)}
          >
            New
          </LoadingButton>
          <Menu anchorEl={anchorEl} open={open} onClose={handleCloseCreationMenu}>
            <MenuItem onClick={handleCreateLegalEntity}>Client (Legal Entity)</MenuItem>
            <MenuItem onClick={handleCreateNaturalPerson}>Client (Natural Person)</MenuItem>
          </Menu>
        </Box>

        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary">
              Total clients
            </Typography>
            <Typography variant="h1">{clientsCount || '-'}</Typography>
          </CardContent>
        </Card>

        <Card className="pt-4">
          <ClientList
            onRowClick={({ row }): void => handleSelectClient(row)}
            columns={[
              {
                field: 'roleplayerName',
                headerName: 'Name',
                flex: 1,
                minWidth: 150,
                renderCell: ({ value, row }) => (
                  <Tooltip
                    placement="left"
                    title={<ClientStatusTooltipContent client={row as unknown as BaseRoleplayer} />}
                  >
                    <Box className="flex space-x-2">
                      <ClientStatusBadge
                        showTooltip={false}
                        client={row as unknown as BaseRoleplayer}
                      />
                      <Typography>{value}</Typography>
                    </Box>
                  </Tooltip>
                ),
              },
              {
                field: 'typeNote',
                headerName: 'Type',
                minWidth: 150,
                flex: 1,
              },
              {
                field: 'roleplayerTaxStatusNote',
                headerName: 'Tax status',
                minWidth: 150,
                flex: 1,
                valueGetter: (_, row) =>
                  // By default cast row to Roleplayer and show default fieldset
                  row.roleplayerTaxStatusNote || row.taxStatusNote,
              },
              {
                field: 'roleplayerIDReg',
                headerName: 'ID number or registration',
                minWidth: 150,
                flex: 1,
              },
              {
                flex: 1,
                minWidth: 150,
                field: 'roleplayerDOBEst',
                headerName: 'Date of birth or registration',
                valueGetter: (_, row) => getFormattedDate(row.roleplayerDOBEst),
              },
            ]}
            getActions={({ row }): ReactElement<GridActionsCellItemProps>[] => [
              <GridActionsCellItem
                key="view"
                color="primary"
                onClick={(): void => handleSelectClient(row)}
                icon={<Icon width={20} icon="mdi:eye" />}
                label="View"
              />,
            ]}
            onGetClients={(clients): void => setClientsCount(clients?.length || 0)}
          />
        </Card>
      </Content>
    </Layout>
  );
};

export default ClientsPage;
